import { useCallback, useEffect, useRef, useState } from 'react';
import { creditsClient, OrgCredits } from '../client/creditsClient';
import { useAuth } from '../contexts/AuthContext';

/** Ledger rows the popover shows — one screenful of recent history, no paging. */
const LEDGER_ROWS = 8;

/**
 * Focus refetches are cheap but not free: alt-tabbing repeatedly should not
 * hammer UMS, so a focus within this window of the last successful load is
 * ignored. There is deliberately NO polling loop.
 */
const FOCUS_REFETCH_MIN_INTERVAL_MS = 15_000;

type Listener = () => void;
const listeners = new Set<Listener>();

/**
 * Invalidate the cached balance after a metered action completes (research
 * start/confirm, outreach send). Module-level on purpose: the call sites live
 * outside the shell (the wizard has its own chrome and no sidebar), so they
 * import this function directly rather than reaching for the hook's state.
 * A no-op when nothing is mounted.
 */
export const invalidateOrgCredits = (): void => {
    listeners.forEach((listener) => listener());
};

export interface OrgCreditsState {
    credits: OrgCredits | null;
    loading: boolean;
    /** True once a load has failed and no earlier data survives — show "—". */
    unreachable: boolean;
    refetch: () => void;
}

/**
 * Org credit balance + recent ledger for the sidebar chip. Never invents a
 * number: a failed load leaves `credits` null and flips `unreachable`, and the
 * chip renders an honest em dash.
 */
export const useOrgCredits = (): OrgCreditsState => {
    const { backendUser } = useAuth();
    const orgId = backendUser?.orgId;

    const [credits, setCredits] = useState<OrgCredits | null>(null);
    const [loading, setLoading] = useState(false);
    const [unreachable, setUnreachable] = useState(false);
    const lastLoadedAt = useRef(0);
    // Guards against a slow in-flight response overwriting a newer one.
    const requestSeq = useRef(0);

    const load = useCallback(async () => {
        if (!orgId) return;
        const seq = ++requestSeq.current;
        setLoading(true);
        try {
            const data = await creditsClient.getOrgCredits(orgId, LEDGER_ROWS);
            if (seq !== requestSeq.current) return;
            setCredits(data);
            setUnreachable(false);
            lastLoadedAt.current = Date.now();
        } catch (err) {
            console.error('Failed to fetch org credits:', err);
            if (seq !== requestSeq.current) return;
            setCredits(null);
            setUnreachable(true);
        } finally {
            if (seq === requestSeq.current) setLoading(false);
        }
    }, [orgId]);

    useEffect(() => {
        // A signed-out / not-yet-synced user has no org: stay blank, not broken.
        if (!orgId) {
            setCredits(null);
            setUnreachable(false);
            return;
        }
        load();
    }, [orgId, load]);

    useEffect(() => {
        if (!orgId) return undefined;
        const onFocus = () => {
            if (Date.now() - lastLoadedAt.current < FOCUS_REFETCH_MIN_INTERVAL_MS) return;
            load();
        };
        window.addEventListener('focus', onFocus);
        listeners.add(load);
        return () => {
            window.removeEventListener('focus', onFocus);
            listeners.delete(load);
        };
    }, [orgId, load]);

    return { credits, loading, unreachable, refetch: load };
};

export default useOrgCredits;
